import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import {
  COMPOSITION_GET_DATA_V2,
  COMPOSITION_GET_DATA_V3,
  type CompositionV2DataQuery,
  type CompositionV2DataQueryVariables,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  Dimension1,
  GET_SCREENER_DATA,
  type GetSentimentDataQuery,
  type GetSentimentDataQueryVariables,
  POSTING_SOURCE_IDS,
  POSTINGS_GET_ACTIVE,
  type PostingsGetActiveQuery,
  type PostingsGetActiveQueryVariables,
  type ScreenerReq,
  type ScreenerRespEntity,
  ScreenerSortCol,
  SENTIMENT_GET_OVERTIME_DATA_V2,
} from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { buildScreenerFilters } from '../../../filters/utils/get-screener-filters';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import { getCustomRoleFilter } from '../../../filters/utils/getCustomRoleFilter';
import { getListFilter } from '../../../filters/utils/getListFilter';
import { getV2GeographyFilters } from '../../../filters/utils/getV2GeographyFilters';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import { PlotFilterType } from '../../PageConfigProvider/types/item';
import {
  isPostingMetric,
  isScreenerMetric,
  isSentimentMetric,
  type MetricValues,
  type PostingMetricValues,
  type ScreenerMetricValues,
  type SentimentMetricValues,
} from '../../PageConfigProvider/types/metric';
import type { MultiMetricProviderResult } from '../MultiMetricProvider';
import { type DataProviderChildren, DataType } from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';
import { processMultipleScreenerMetrics } from '../utils/screener-metrics-utils';
import { useSnowflakeSavedQuery } from '../utils/useSnowflakeSavedQuery';

const DEFAULT_DISCOVERY_LIMIT = 10;

type EffectiveFilter = ReturnType<typeof useEffectiveFilters>[number];

const buildSecondaryFilters = (effectiveFilters: EffectiveFilter[]) => {
  const roleFilter = findFilterById(effectiveFilters, FilterId.ROLE);
  const geoFilter = findFilterById(effectiveFilters, FilterId.GEOGRAPHY);
  const seniorityFilter = findFilterById(effectiveFilters, FilterId.SENIORITY);
  const geo = geoFilter ? getV2GeographyFilters(geoFilter) : {};

  return {
    ...(roleFilter ? getCustomRoleFilter(roleFilter, effectiveFilters) : {}),
    ...(geo.region ? { region: geo.region } : {}),
    ...(geo.country ? { country: geo.country } : {}),
    ...(geo.msa ? { msa: geo.msa } : {}),
    ...(seniorityFilter?.type === FilterType.LIST
      ? getListFilter(seniorityFilter, 'seniority')
      : {}),
  };
};

const getLatestFromSeries = (
  series: ReadonlyArray<{ value?: number | null } | null> | null | undefined
): number | null => {
  if (!Array.isArray(series) || series.length === 0) return null;
  const last = series[series.length - 1];
  return typeof last?.value === 'number' ? last.value : null;
};

type PostingCategory = NonNullable<
  NonNullable<PostingsGetActiveQuery['posting']>[number]
>['category'];

const getLatestPostingCount = (categories: PostingCategory): number | null => {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const latest = [...categories]
    .filter((c) => c?.metadata?.shortName)
    .sort((a, b) =>
      (b?.metadata?.shortName ?? '').localeCompare(a?.metadata?.shortName ?? '')
    )[0];
  return typeof latest?.metrics?.active === 'number'
    ? Math.round(latest.metrics.active)
    : null;
};

const extractSentimentMetricValues = (
  sentimentEntities: NonNullable<GetSentimentDataQuery['sentiment']>,
  screenerEntities: ScreenerRespEntity[],
  metric: SentimentMetricValues
): Array<{ dim: string; value: number }> =>
  screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const sentimentEntity = sentimentEntities.find(
      (se) => se?.metadata?.id === entity.entity_id
    );

    const scores = sentimentEntity?.result?.scores as
      | Record<string, ReadonlyArray<{ value?: number | null } | null> | null>
      | null
      | undefined;

    const value = getLatestFromSeries(scores?.[metric]);
    if (value === null) return [];

    return [{ dim: label, value: value / 100 }];
  });

const extractActivePostings = (
  postingsEntities: NonNullable<PostingsGetActiveQuery['posting']>,
  screenerEntities: ScreenerRespEntity[]
): Array<{ dim: string; value: number }> =>
  screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const posting = postingsEntities.find(
      (p) => p?.metadata?.id === entity.entity_id
    );
    const count = getLatestPostingCount(posting?.category);
    return count !== null ? [{ dim: label, value: count }] : [];
  });

const buildPostingsByRcid = (
  postingsEntities: NonNullable<PostingsGetActiveQuery['posting']>
): Map<number, number> => {
  const map = new Map<number, number>();
  for (const posting of postingsEntities) {
    const rcid = posting?.metadata?.id;
    if (rcid == null) continue;
    const count = getLatestPostingCount(posting?.category);
    if (count !== null) {
      map.set(rcid, count);
    }
  }
  return map;
};

const extractMarketShare = (
  screenerEntities: ScreenerRespEntity[],
  headcountByCompany: Map<string, number>,
  totalHeadcount: number | null
): Array<{ dim: string; value: number }> =>
  screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const companyHeadcount = headcountByCompany.get(label) ?? null;
    if (
      companyHeadcount === null ||
      totalHeadcount === null ||
      totalHeadcount <= 0
    ) {
      return [];
    }
    return [{ dim: label, value: companyHeadcount / totalHeadcount }];
  });

type SnowflakeRow = Record<string, unknown>;

const quantile = (values: number[], q: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  const baseVal = sorted[base] ?? 0;
  const nextVal = sorted[base + 1];
  return nextVal !== undefined ? baseVal + rest * (nextVal - baseVal) : baseVal;
};

const findTargetCompanies = (rows: SnowflakeRow[]): string[] => {
  const companies = rows.map((row) => ({
    name: String(row['Company'] ?? ''),
    salary:
      typeof row['Salary'] === 'number' ? (row['Salary'] as number) : null,
    sentiment:
      typeof row['Sentiment Rating'] === 'number'
        ? (row['Sentiment Rating'] as number)
        : null,
    prestige: String(row['Prestige'] ?? ''),
  }));

  const salaries = companies
    .map((c) => c.salary)
    .filter((v): v is number => v !== null);
  const sentiments = companies
    .map((c) => c.sentiment)
    .filter((v): v is number => v !== null);
  const highPrestigeNames = new Set(
    companies
      .filter((c) => c.prestige === 'High' || c.prestige === 'Very High')
      .map((c) => c.name)
  );

  const findAtThreshold = (salaryQ: number, sentimentQ: number) => {
    const salaryThreshold =
      salaries.length > 0 ? quantile(salaries, salaryQ) : 0;
    const sentimentThreshold =
      sentiments.length > 0 ? quantile(sentiments, sentimentQ) : 0;
    return companies
      .filter(
        (c) =>
          c.salary !== null &&
          c.salary < salaryThreshold &&
          c.sentiment !== null &&
          c.sentiment < sentimentThreshold &&
          highPrestigeNames.has(c.name)
      )
      .map((c) => c.name);
  };

  const targets = findAtThreshold(0.33, 0.33);
  return targets.length > 0 ? targets : findAtThreshold(0.5, 0.5);
};

const buildSnowflakeTableRows = (
  snowflakeRows: SnowflakeRow[],
  postingsByRcid: Map<number, number>,
  headcountColumnName: string
) => {
  const rows = snowflakeRows.map((row) => {
    const rcid = row['rcid'];
    return {
      Company: String(row['Company'] ?? ''),
      [headcountColumnName]: row['Headcount'],
      'Market Share':
        typeof row['Market Share'] === 'number'
          ? (row['Market Share'] as number) / 100
          : null,
      Salary: row['Salary'],
      'Active Postings':
        typeof rcid === 'number' ? (postingsByRcid.get(rcid) ?? null) : null,
      'Sentiment Rating': row['Sentiment Rating'],
      prestige: row['Prestige'] ?? '',
    };
  });

  const columnMetrics: Record<string, string> = {
    [headcountColumnName]: 'screener_headcount',
    'Market Share': 'market_share',
    Salary: 'screener_salary',
    'Active Postings': 'postings_active',
    'Sentiment Rating': 'demand_supply',
  };

  const columnFormats: Record<string, string> = {
    'Market Share': '.2%',
  };

  return { rows, columnMetrics, columnFormats };
};

const extractSnowflakeMarketShare = (
  snowflakeRows: SnowflakeRow[]
): Array<{ dim: string; value: number }> =>
  snowflakeRows.flatMap((row) => {
    const dim = String(row['Company'] ?? '');
    const value = row['Market Share'];
    return typeof value === 'number' ? [{ dim, value: value / 100 }] : [];
  });

const getTotalHeadcount = (
  compositionData: CompositionV2DataQuery | CompositionV3DataQuery | undefined,
  useCompositionV3: boolean
): number | null => {
  if (useCompositionV3) {
    const v21 = compositionData as CompositionV3DataQuery | undefined;
    const headcountTs = v21?.composition_v3?.entities?.[0]?.metrics?.headcount;
    if (!Array.isArray(headcountTs) || headcountTs.length === 0) return null;
    const last = headcountTs[headcountTs.length - 1];
    return typeof last?.val === 'number' ? last.val : null;
  }
  const v2 = compositionData as CompositionV2DataQuery | undefined;
  const headcountTs = v2?.composition_v2?.entities?.[0]?.metrics?.headcount;
  if (!Array.isArray(headcountTs) || headcountTs.length === 0) return null;
  const last = headcountTs[headcountTs.length - 1];
  return typeof last?.value === 'number' ? last.value : null;
};

/**
 * Fetches screener data to discover top entities, then optionally chains
 * sentiment, postings, and composition queries for those entities. Returns
 * each requested metric as its own DataType.SINGLE entry in the byMetric
 * map so the standard table pipeline can merge columns.
 */
type CompetitiveLandscapeCompositionApi = 'legacy' | 'v3';

type CompetitiveLandscapeProviderInnerProps = {
  compositionApi: CompetitiveLandscapeCompositionApi;
  primaryDimension: PrimaryFilterId;
  metrics: MetricValues[];
  children: DataProviderChildren<MultiMetricProviderResult>;
};

export const CompetitiveLandscapeProviderLegacy = (
  props: Omit<CompetitiveLandscapeProviderInnerProps, 'compositionApi'>
) => <CompetitiveLandscapeProviderInner compositionApi="legacy" {...props} />;

export const CompetitiveLandscapeProviderV3 = (
  props: Omit<CompetitiveLandscapeProviderInnerProps, 'compositionApi'>
) => <CompetitiveLandscapeProviderInner compositionApi="v3" {...props} />;

export const CompetitiveLandscapeProvider = (
  props: Omit<CompetitiveLandscapeProviderInnerProps, 'compositionApi'>
) => {
  const isV3 = useUnleashFlag(FeatureFlag.CompositionV3);
  if (isV3) {
    return <CompetitiveLandscapeProviderV3 {...props} />;
  }
  return <CompetitiveLandscapeProviderLegacy {...props} />;
};

const CompetitiveLandscapeProviderInner = ({
  compositionApi,
  primaryDimension,
  metrics,
  children,
}: CompetitiveLandscapeProviderInnerProps) => {
  const { item } = useChart();
  const { config: tabConfig } = useTabContentConfig();

  // Resolve the Snowflake config for competitive landscape data by finding
  // the table item that shares the same dimensionSource. This lets text
  // charts reuse the table's config without duplicating it.
  const snowflakeConfig = tabConfig.items.find(
    (sibling) =>
      sibling.plot?.dimensionSource === item?.plot?.dimensionSource &&
      sibling.plot?.chartType === 'table' &&
      sibling.plot?.snowflakeConfig
  )?.plot?.snowflakeConfig;
  const effectiveFilters = useEffectiveFilters();
  const compositionEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'month',
  });

  const discoveryLimit =
    item?.plot?.plotFilters?.find((f) => f.type === PlotFilterType.TOP_N)
      ?.value ?? DEFAULT_DISCOVERY_LIMIT;

  const roleFilter = effectiveFilters.find(
    (f) => f.id === FilterId.ROLE && f.type === FilterType.TREE
  );
  const headcountColumnName =
    roleFilter?.type === FilterType.TREE && roleFilter.selections?.[0]?.name
      ? `${roleFilter.selections[0].name} Roles`
      : 'Headcount';

  const screenerMetrics = metrics.filter((m): m is ScreenerMetricValues =>
    isScreenerMetric(m)
  );
  const sentimentMetrics = metrics.filter((m): m is SentimentMetricValues =>
    isSentimentMetric(m)
  );
  const postingMetrics = metrics.filter((m): m is PostingMetricValues =>
    isPostingMetric(m)
  );
  const needsMarketShare = metrics.includes('market_share');

  const secondaryFilters = buildSecondaryFilters(effectiveFilters);

  // ── Screener query (entity discovery) ──
  const screenerFilters: ScreenerReq = (() => {
    const firstMetric = screenerMetrics[0] ?? 'screener_headcount';
    const base = buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric: firstMetric,
      pagination: { limit: discoveryLimit, offset: 0 },
      sorting: {
        sort_by: ScreenerSortCol.SortByHeadcount,
        ascending: false,
        num_col: 1,
      },
    });

    const headcountSegments = base.segments?.headcount;
    const updatedBase =
      Array.isArray(headcountSegments) && headcountSegments.length > 0
        ? {
            ...base,
            segments: {
              ...base.segments,
              salary: headcountSegments.map((seg) => ({
                ...seg,
                start_val: 0,
              })),
            },
          }
        : base;

    return {
      ...updatedBase,
      entity_info_selectors: {
        ...updatedBase.entity_info_selectors,
        company_info_selectors: {
          ...updatedBase.entity_info_selectors?.company_info_selectors,
          is_subsidiary: false,
        },
      },
    };
  })();

  const [{ data: screenerData, status: screenerStatus }] =
    useUrqlQueryWithStatus<
      { screener: { entities: ScreenerRespEntity[] } },
      { filters: ScreenerReq }
    >({
      query: GET_SCREENER_DATA,
      variables: { filters: screenerFilters },
      pause: !screenerFilters.dim,
    });

  const entities = screenerData?.screener?.entities ?? [];
  const companyIds = entities.map((e) => e.entity_id);
  const hasEntities = companyIds.length > 0;

  // ── Sentiment query (chained) ──
  const shouldFetchSentiment = sentimentMetrics.length > 0 && hasEntities;

  const [{ data: sentimentData, status: sentimentStatus }] =
    useUrqlQueryWithStatus<
      GetSentimentDataQuery,
      GetSentimentDataQueryVariables
    >({
      query: SENTIMENT_GET_OVERTIME_DATA_V2,
      variables: {
        dim1: Dimension1.Company,
        filters: { company: companyIds, ...secondaryFilters },
      },
      pause: !shouldFetchSentiment,
    });

  // ── Snowflake query (sentiment rating + prestige + core columns) ──
  const {
    data: snowflakeRows,
    status: snowflakeStatus,
    enabled: snowflakeEnabled,
  } = useSnowflakeSavedQuery(snowflakeConfig);

  // ── Postings query (chained) ──
  // When Snowflake data is available, use Snowflake rcids for postings lookup
  const snowflakeRcids = (snowflakeRows ?? [])
    .map((r) => r['rcid'])
    .filter((id): id is number => typeof id === 'number');
  const postingsCompanyIds =
    snowflakeConfig && snowflakeRcids.length > 0 ? snowflakeRcids : companyIds;
  const hasPostingsCompanies = postingsCompanyIds.length > 0;
  const shouldFetchPostings = postingMetrics.length > 0 && hasPostingsCompanies;

  const [{ data: postingsData, status: postingsStatus }] =
    useUrqlQueryWithStatus<
      PostingsGetActiveQuery,
      PostingsGetActiveQueryVariables
    >({
      query: POSTINGS_GET_ACTIVE,
      variables: {
        dim1: Dimension1.Company,
        filters: {
          company: postingsCompanyIds,
          provider: [POSTING_SOURCE_IDS.unified],
          ...secondaryFilters,
        },
      },
      pause: !shouldFetchPostings,
    });

  // ── Composition query (for market share denominator) ──
  const filtersForComposition = compositionEffectiveFilters.filter(
    (f) => f.id !== FilterId.COMPANY
  );
  const compositionVars = getCompositionV2Filters({
    primaryDimension: FilterId.ROLE,
    effectiveFilters: filtersForComposition,
    isSnapshot: true,
  });

  const compositionVarsV3: CompositionV3DataQueryVariables =
    getCompositionV3Variables({
      primaryDimension: FilterId.ROLE,
      effectiveFilters: filtersForComposition,
      isSnapshot: true,
    });

  const compositionPaused =
    !needsMarketShare ||
    !hasEntities ||
    (Array.isArray(compositionVars.entities) &&
      compositionVars.entities.length === 0) ||
    !compositionVars.filters?.start_date ||
    !compositionVars.filters?.end_date;

  const compositionPausedV3 =
    !needsMarketShare ||
    !hasEntities ||
    (Array.isArray(compositionVarsV3.entities) &&
      compositionVarsV3.entities.length === 0) ||
    !compositionVarsV3.filters?.start_date ||
    !compositionVarsV3.filters?.end_date;

  const useCompositionV3 = compositionApi === 'v3';

  const compositionQuery = useCompositionV3
    ? COMPOSITION_GET_DATA_V3
    : COMPOSITION_GET_DATA_V2;
  const compositionVariables = useCompositionV3
    ? compositionVarsV3
    : compositionVars;
  const compositionPausedActive = useCompositionV3
    ? compositionPausedV3
    : compositionPaused;

  const [{ data: compositionDataUnion, status: compositionStatusUnion }] =
    useUrqlQueryWithStatus<
      CompositionV2DataQuery | CompositionV3DataQuery,
      CompositionV2DataQueryVariables | CompositionV3DataQueryVariables
    >({
      query: compositionQuery,
      variables: compositionVariables,
      pause: compositionPausedActive,
    });

  const compositionDataForHeadcount = compositionDataUnion;

  const compositionStatusForCombine = compositionStatusUnion;

  const compositionFetchPaused = compositionPausedActive;

  // ── Build byMetric map ──
  const processedScreener = processMultipleScreenerMetrics(
    entities,
    screenerMetrics
  );
  const headcountByCompany = new Map(
    processMultipleScreenerMetrics(entities, ['screener_headcount'])[
      'screener_headcount'
    ]?.map((p) => [p.dim, p.value]) ?? []
  );
  const totalHeadcount = getTotalHeadcount(
    compositionDataForHeadcount,
    useCompositionV3
  );

  const sentimentResults = sentimentData?.sentiment ?? [];
  const postingsResults = postingsData?.posting ?? [];

  const getMetricData = (
    m: MetricValues
  ): Array<{ dim: string; value: number }> => {
    if (isScreenerMetric(m)) {
      return processedScreener[m] ?? [];
    }
    if (isSentimentMetric(m)) {
      return extractSentimentMetricValues(sentimentResults, entities, m);
    }
    if (m === 'postings_active') {
      return extractActivePostings(postingsResults, entities);
    }
    if (m === 'market_share') {
      return extractMarketShare(entities, headcountByCompany, totalHeadcount);
    }
    return [];
  };

  const byMetric: MultiMetricProviderResult['byMetric'] = {};

  if (snowflakeConfig && snowflakeRows && snowflakeRows.length > 0) {
    // ── Snowflake-primary mode ──
    const postingsByRcid = buildPostingsByRcid(postingsResults);
    const { rows, columnMetrics, columnFormats } = buildSnowflakeTableRows(
      snowflakeRows,
      postingsByRcid,
      headcountColumnName
    );

    const targetCompanies = findTargetCompanies(snowflakeRows);
    if (targetCompanies.length > 0) {
      byMetric['target_companies_rec'] = {
        type: DataType.SINGLE,
        data: targetCompanies.map((name, i) => ({
          dim: name,
          value: targetCompanies.length - i,
        })),
      };
    }

    byMetric['market_share'] = {
      type: DataType.SINGLE,
      data: extractSnowflakeMarketShare(snowflakeRows),
    };

    byMetric['screener_headcount'] = {
      type: DataType.TABLE_ROWS,
      data: rows.map((row) => ({
        ...row,
        __columnMetrics: columnMetrics,
        __columnFormats: columnFormats,
      })),
    };
  } else {
    const headcountData = getMetricData('screener_headcount');
    const metricDataByCompany = new Map<string, Record<string, unknown>>();

    for (const item of headcountData) {
      metricDataByCompany.set(item.dim, {
        Company: item.dim,
        [headcountColumnName]: item.value,
      });
    }

    const columnMetrics: Record<string, string> = {
      [headcountColumnName]: 'screener_headcount',
    };

    for (const m of metrics) {
      if (m === 'screener_headcount') continue;
      if (m === 'market_share' && snowflakeConfig) continue;
      const metricData = getMetricData(m);
      const metricLabelMap: Record<string, string> = {
        market_share: 'Market Share',
        screener_salary: 'Salary',
        postings_active: 'Active Postings',
        overall: 'Sentiment Rating',
      };
      const label = metricLabelMap[m] ?? m;
      columnMetrics[label] = m;

      for (const d of metricData) {
        const row = metricDataByCompany.get(d.dim);
        if (row) {
          row[label] = d.value;
        }
      }
    }

    const columnFormats: Record<string, string> = {
      'Market Share': '.2%',
    };

    const rows = Array.from(metricDataByCompany.values());

    byMetric['screener_headcount'] = {
      type: DataType.TABLE_ROWS,
      data: rows.map((row) => ({
        ...row,
        __columnMetrics: columnMetrics,
        __columnFormats: columnFormats,
      })),
    };
  }

  // ── Status ──
  const statuses = [screenerStatus];
  if (shouldFetchSentiment) statuses.push(sentimentStatus);
  if (shouldFetchPostings) statuses.push(postingsStatus);
  if (!compositionFetchPaused) statuses.push(compositionStatusForCombine);
  if (snowflakeEnabled) statuses.push(snowflakeStatus);
  const status = combineQueryStatus(statuses);

  return children({
    data: { byMetric },
    status,
  });
};
